import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowCounterClockwise, Trash, Warning, CheckCircle, Database, Users as UsersIcon,
  CalendarBlank, Eye,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import OverpaidPaymentsCard from "./OverpaidPaymentsCard";

/** Settings → Data: recover from bad imports.
 *
 *  Two ways to clean up:
 *   1. UNDO BATCH — each import is tagged with a batch_id; Undo removes
 *      customers from that batch who haven't transacted yet.
 *   2. DATE-RANGE CLEANUP — one-shot deletion of untransacted customers
 *      created in a specific window (for historical bad imports that predate
 *      batch tagging).
 *
 *  Both flows skip customers with attached tickets / sales / bookings.
 *  Both are admin + PIN gated.
 */
export default function DataTab() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [saleBatches, setSaleBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Date-range cleanup
  const todayISO = new Date().toISOString().slice(0, 10);
  const [since, setSince] = useState(todayISO);
  const [until, setUntil] = useState(todayISO);
  const [importedOnly, setImportedOnly] = useState(true);
  const [deleteImportedSales, setDeleteImportedSales] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [pinPrompt, setPinPrompt] = useState(null); // { kind, payload }
  const [backfilling, setBackfilling] = useState(false);
  const [orphanCount, setOrphanCount] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [unifying, setUnifying] = useState(false);
  const [dupScan, setDupScan] = useState(null); // { count, tickets }
  const [dupLoading, setDupLoading] = useState(false);
  const [dupSelected, setDupSelected] = useState(() => new Set());
  const [saleDupScan, setSaleDupScan] = useState(null); // { count, tickets } — duplicate POS sales
  const [histStats, setHistStats] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  const isAdmin = user?.role === "admin";

  const scanDuplicates = async () => {
    setDupLoading(true);
    try {
      const [{ data }, { data: saleData }] = await Promise.all([
        api.get("/invoice-duplicates"),
        api.get("/sale-duplicates"),
      ]);
      setDupScan(data);
      setSaleDupScan(saleData);
      // Pre-select every non-keep invoice so cleanup is one click.
      const preset = new Set();
      (data.tickets || []).forEach((t) => t.invoices.forEach((i) => { if (i.id !== t.keep_id) preset.add(i.id); }));
      setDupSelected(preset);
      if (data.count === 0 && saleData.count === 0) toast.success("No duplicate invoices found — all tickets are clean.");
    } catch (e) { toast.error(formatApiError(e) || "Scan failed"); }
    finally { setDupLoading(false); }
  };

  const toggleDupSelect = (id) => {
    setDupSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const backfillTags = async () => {
    if (!window.confirm("Tag historical RepairDesk imports so they show up in the delete tools below? This is safe — nothing is deleted.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/imports/backfill-tags");
      if (data.sales_tagged + data.customers_tagged === 0) {
        toast.info("No untagged imports found — everything is already labelled.");
      } else {
        toast.success(`Tagged ${data.sales_tagged} invoices and ${data.customers_tagged} customers. Scroll down to undo or delete them.`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e) || "Backfill failed"); }
    finally { setBackfilling(false); }
  };

  const deleteAllImports = () => {
    setPinPrompt({
      kind: "delete-all",
      label: "Delete ALL imported invoices and their import-only customers across every store?",
    });
  };

  const previewOrphans = async () => {
    setLoadingOrphans(true);
    try {
      const { data } = await api.get("/customers-orphan-count");
      setOrphanCount(data);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't count orphans"); }
    finally { setLoadingOrphans(false); }
  };

  const deleteOrphans = () => {
    if (!orphanCount || orphanCount.orphans === 0) return;
    setPinPrompt({
      kind: "delete-orphans",
      label: `Delete all ${orphanCount.orphans} customers with zero transactions?`,
    });
  };

  const loadHistStats = async () => {
    setLoadingHist(true);
    try {
      const { data } = await api.get("/integrations/historical-summary-stats");
      setHistStats(data);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't load"); }
    finally { setLoadingHist(false); }
  };

  const clearHistSummaries = () => {
    if (!histStats || histStats.count === 0) return;
    setPinPrompt({
      kind: "clear-hist-summaries",
      label: `Delete ${histStats.count} historical revenue summary records (total $${histStats.revenue_sum.toLocaleString()})?`,
    });
  };

  useEffect(() => { if (isAdmin) loadHistStats(); }, [isAdmin]);

  const runDiagnostics = async () => {
    setLoadingDiag(true);
    try {
      const { data } = await api.get("/imports/diagnostics");
      setDiagnostics(data);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't load diagnostics"); }
    finally { setLoadingDiag(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/customer-import-batches"),
        api.get("/sale-import-batches"),
      ]);
      setBatches(c.data);
      setSaleBatches(s.data);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't load import history"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const runPreview = async () => {
    if (!since || !until) return;
    setPreviewing(true);
    try {
      // convert date strings to inclusive range (since = start of day; until = start of next day)
      const sinceISO = new Date(since + "T00:00:00").toISOString();
      const untilISO = new Date(until + "T00:00:00");
      untilISO.setDate(untilISO.getDate() + 1);
      const { data } = await api.post("/customers-bulk-delete-preview", {
        since: sinceISO, until: untilISO.toISOString(),
        imported_only: importedOnly,
        delete_imported_sales: deleteImportedSales,
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e) || "Couldn't preview"); }
    finally { setPreviewing(false); }
  };

  const undoBatch = (batch) => {
    if (batch.undone_at) return;
    setPinPrompt({ kind: "undo-batch", payload: batch, label: `Undo "${batch.count}-row" import from ${fmtDate(batch.imported_at)}?` });
  };

  const undoSaleBatch = (batch) => {
    if (batch.undone_at) return;
    setPinPrompt({
      kind: "undo-sale-batch",
      payload: batch,
      label: `Delete all ${batch.count} invoices from the import on ${fmtDate(batch.imported_at)}?`,
    });
  };

  const deleteByDate = () => {
    if (!preview || preview.deletable === 0) return;
    setPinPrompt({
      kind: "bulk-date",
      payload: null,
      label: `Delete ${preview.deletable} customers created between ${since} and ${until}?`,
    });
  };

  const submitPin = async (pin) => {
    if (!pin) return;
    try {
      if (pinPrompt.kind === "undo-batch") {
        const { data } = await api.post(
          `/customer-import-batches/${pinPrompt.payload.id}/undo`,
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        toast.success(`Deleted ${data.deleted} customers${data.preserved ? `, preserved ${data.preserved} with transactions` : ""}`);
        await load();
      } else if (pinPrompt.kind === "undo-sale-batch") {
        const { data } = await api.post(
          `/sale-import-batches/${pinPrompt.payload.id}/undo`,
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        toast.success(`Deleted ${data.deleted} imported invoices`);
        await load();
      } else if (pinPrompt.kind === "delete-all") {
        const { data } = await api.post(
          "/imports/delete-all-imported",
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        const parts = [`Deleted ${data.sales_deleted} imported invoices`];
        if (data.customers_deleted) parts.push(`${data.customers_deleted} import-only customers`);
        if (data.customers_preserved) parts.push(`preserved ${data.customers_preserved} with transactions`);
        toast.success(parts.join(", "));
        await load();
      } else if (pinPrompt.kind === "delete-orphans") {
        const { data } = await api.post(
          "/customers-delete-orphans",
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        toast.success(`Deleted ${data.deleted} orphan customers, preserved ${data.preserved} with transactions`);
        setOrphanCount(null);
        await load();
      } else if (pinPrompt.kind === "clear-hist-summaries") {
        const { data } = await api.post(
          "/integrations/historical-summary/clear-all",
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        toast.success(`Cleared ${data.deleted} historical summary records. Reports will now reflect only real sales.`);
        await loadHistStats();
      } else if (pinPrompt.kind === "bulk-date") {
        const sinceISO = new Date(since + "T00:00:00").toISOString();
        const untilISO = new Date(until + "T00:00:00");
        untilISO.setDate(untilISO.getDate() + 1);
        const { data } = await api.post(
          "/customers-bulk-delete-by-date",
          {
            since: sinceISO, until: untilISO.toISOString(),
            imported_only: importedOnly,
            delete_imported_sales: deleteImportedSales,
          },
          { headers: { "X-Admin-Pin": pin } }
        );
        const parts = [`Deleted ${data.deleted} customers`];
        if (data.preserved) parts.push(`preserved ${data.preserved} with transactions`);
        if (data.sales_deleted) parts.push(`removed ${data.sales_deleted} imported invoices`);
        toast.success(parts.join(", "));
        setPreview(null);
        await load();
      } else if (pinPrompt.kind === "unify-invoices") {
        setUnifying(true);
        const { data } = await api.post(
          "/online-invoices/unify-numbers",
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        setUnifying(false);
        toast.success(data.updated > 0 ? `Renumbered ${data.updated} legacy invoice(s) to the S- series` : "No legacy invoices found — already unified");
      } else if (pinPrompt.kind === "void-duplicate") {
        const inv = pinPrompt.payload;
        const { data } = await api.post(
          `/invoice-duplicates/${inv.id}/void`,
          {},
          { headers: { "X-Admin-Pin": pin } }
        );
        if (data.refund_owed > 0.005) {
          toast.warning(`Voided ${data.invoice_number}. It had $${data.refund_owed.toFixed(2)} paid on it — refund the customer if that money was collected twice.`, { duration: 8000 });
        } else {
          toast.success(`Voided duplicate ${data.invoice_number}`);
        }
        await scanDuplicates();
      } else if (pinPrompt.kind === "void-duplicate-sale") {
        const sale = pinPrompt.payload;
        await api.post(
          `/sales/${sale.id}/void`,
          { reason: "duplicate invoice cleanup" },
          { headers: { "X-Admin-Pin": pin } }
        );
        if (sale.paid > 0.005) {
          toast.warning(`Voided ${sale.sale_number}. It had $${sale.paid.toFixed(2)} paid on it — refund the customer if that money was collected twice.`, { duration: 8000 });
        } else {
          toast.success(`Voided duplicate invoice ${sale.sale_number}`);
        }
        await scanDuplicates();
      } else if (pinPrompt.kind === "void-duplicate-bulk") {
        const ids = pinPrompt.payload;
        const { data } = await api.post(
          `/invoice-duplicates/void-bulk`,
          { invoice_ids: ids },
          { headers: { "X-Admin-Pin": pin } }
        );
        let msg = `Voided ${data.voided} duplicate invoice${data.voided === 1 ? "" : "s"}`;
        if (data.total_refund_owed > 0.005) msg += ` — $${data.total_refund_owed.toFixed(2)} may need refunding`;
        (data.total_refund_owed > 0.005 ? toast.warning : toast.success)(msg, { duration: 8000 });
        if ((data.skipped || []).length) toast.info(`${data.skipped.length} skipped (kept as each ticket's last invoice)`);
        await scanDuplicates();
      }
      setPinPrompt(null);
    } catch (e) {
      setUnifying(false);
      toast.error(formatApiError(e) || "Couldn't complete action");
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-slate-500">
          <Database size={32} weight="duotone" className="mx-auto mb-2 text-slate-300" />
          Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-8 py-6 space-y-6">
      {/* Database diagnostics — read-only snapshot so the user can see the raw numbers */}
      <section className="rounded-2xl border border-slate-300 bg-slate-900 text-slate-100 overflow-hidden" data-testid="diagnostics-card">
        <div className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-800 text-cyan-300 flex items-center justify-center shrink-0">
            <Database size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-300 font-semibold">DATABASE SNAPSHOT</div>
            <div className="font-heading font-bold text-[15px] text-slate-100 tracking-tight mt-0.5">
              What's actually stored right now
            </div>
            <div className="text-[12px] text-slate-400 mt-0.5 mb-3">
              Run this to see exactly what data drives your Reports totals — no database shell needed.
            </div>
            <button
              onClick={runDiagnostics}
              disabled={loadingDiag}
              data-testid="run-diagnostics-btn"
              className="h-10 px-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Eye size={13} weight="bold" />
              {loadingDiag ? "Scanning…" : "Run diagnostics"}
            </button>

            {diagnostics && (
              <div className="mt-4 space-y-4 text-[12px] font-mono">
                <div className="rounded-lg bg-slate-800 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold mb-1.5">HISTORICAL_SUMMARIES (aggregated "history" revenue)</div>
                  {diagnostics.historical_summaries.count === 0 ? (
                    <div className="text-emerald-400">✅ Empty — no history being added to reports.</div>
                  ) : (
                    <>
                      <div>
                        <span className="text-slate-400">Rows:</span> <span className="text-yellow-300 font-bold">{diagnostics.historical_summaries.count}</span>
                        <span className="text-slate-400 ml-4">Sum: </span><span className="text-yellow-300 font-bold">${diagnostics.historical_summaries.revenue_sum.toLocaleString()}</span>
                      </div>
                      <div className="mt-2 space-y-1 text-[11px]">
                        {diagnostics.historical_summaries.rows.slice(0, 10).map((r) => (
                          <div key={r.id} className="text-slate-300">
                            {(r.period_start || "").slice(0, 10)} → {(r.period_end || "").slice(0, 10)} · ${(r.total_sales || 0).toLocaleString()} · store={(r.store_id || "").slice(0, 8)}
                          </div>
                        ))}
                        {diagnostics.historical_summaries.rows.length > 10 && (
                          <div className="text-slate-500">…plus {diagnostics.historical_summaries.rows.length - 10} more</div>
                        )}
                      </div>
                      <button
                        onClick={clearHistSummaries}
                        data-testid="diag-clear-hist-btn"
                        className="mt-3 h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold inline-flex items-center gap-1.5"
                      >
                        <Trash size={12} weight="bold" />
                        Delete all {diagnostics.historical_summaries.count} rows
                      </button>
                    </>
                  )}
                </div>

                <div className="rounded-lg bg-slate-800 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold mb-1.5">IMPORTED SALES (by month, newest first)</div>
                  {diagnostics.imported_sales.total === 0 ? (
                    <div className="text-slate-400">No imported sales in DB.</div>
                  ) : (
                    <>
                      <div className="mb-2">
                        <span className="text-slate-400">Total imported:</span> <span className="text-cyan-300 font-bold">{diagnostics.imported_sales.total.toLocaleString()}</span>
                      </div>
                      {diagnostics.imported_sales.dated_in_current_month_count > 0 && (
                        <div className="mb-3 p-2 rounded bg-rose-900/50 border border-rose-700 text-rose-200">
                          ⚠️ <b>{diagnostics.imported_sales.dated_in_current_month_count}</b> imported sales are dated in the current month ({diagnostics.imported_sales.current_month}) — worth ${diagnostics.imported_sales.dated_in_current_month_revenue.toLocaleString()}. This is likely the source of your inflated "this month" revenue. Re-import after the date-parsing fix, OR use the Legacy Cleanup above to delete + reimport.
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[11px]">
                        {diagnostics.imported_sales.buckets_by_month.map((b) => (
                          <div key={b.month} className={`p-1.5 rounded ${b.month === diagnostics.imported_sales.current_month ? "bg-rose-900/60 text-rose-100" : "bg-slate-700/50"}`}>
                            <div className="text-slate-400">{b.month}</div>
                            <div className="font-bold">{b.count} · ${b.revenue.toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Legacy cleanup — for imports done before batch tagging */}
      <section className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white overflow-hidden" data-testid="legacy-cleanup-card">
        <div className="p-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
            <Warning size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-rose-700 font-semibold">LEGACY CLEANUP</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Fix untagged RepairDesk imports
            </div>
            <div className="text-[12px] text-slate-600 mt-0.5 mb-4">
              If your RepairDesk import happened <em>before</em> we added batch tracking, the filter below won't find it. Run step 1 to tag those older imports so they show up, then either Undo them from the tables below — or step 2 nukes everything flagged as imported in one shot.
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={backfillTags}
                disabled={backfilling}
                data-testid="backfill-tags-btn"
                className="h-10 px-4 rounded-lg border border-rose-200 hover:border-rose-400 bg-white text-rose-700 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Database size={13} weight="bold" />
                {backfilling ? "Tagging…" : "Step 1 — Tag old imports"}
              </button>
              <button
                onClick={deleteAllImports}
                data-testid="delete-all-imports-btn"
                className="h-10 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-bold inline-flex items-center gap-1.5"
              >
                <Trash size={13} weight="bold" />
                Step 2 — Delete ALL imported invoices + customers
              </button>
            </div>

            {/* Step 3: orphan cleanup */}
            <div className="mt-5 pt-4 border-t border-rose-100">
              <div className="text-[11px] uppercase tracking-[0.18em] text-rose-700 font-semibold mb-0.5">STEP 3 — FINAL MOP-UP</div>
              <div className="text-[12px] text-slate-600 mb-3">
                After Step 2, any remaining untagged customers (like the 13k+ you're seeing) can be removed in one shot. This looks at ALL customers and deletes those with zero tickets, zero sales, and zero bookings — totally safe.
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={previewOrphans}
                  disabled={loadingOrphans}
                  data-testid="orphan-preview-btn"
                  className="h-10 px-4 rounded-lg border border-rose-200 hover:border-rose-400 bg-white text-rose-700 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Eye size={13} weight="bold" />
                  {loadingOrphans ? "Counting…" : "Count orphans"}
                </button>
                {orphanCount && (
                  <>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="text-[12px] text-slate-600">
                        <span className="font-mono font-bold text-rose-700 tabular-nums">{orphanCount.orphans.toLocaleString()}</span> orphan customers found,
                        <span className="font-mono font-bold text-slate-700 tabular-nums ml-1">{orphanCount.with_transactions.toLocaleString()}</span> will be preserved
                      </div>
                    </div>
                    {orphanCount.orphans > 0 && (
                      <button
                        onClick={deleteOrphans}
                        data-testid="orphan-delete-btn"
                        className="h-10 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-bold inline-flex items-center gap-1.5"
                      >
                        <Trash size={13} weight="bold" />
                        Delete {orphanCount.orphans.toLocaleString()} orphans
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical revenue summaries — only shows when data exists */}
      {histStats && histStats.count > 0 && (
        <section className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white overflow-hidden" data-testid="hist-summaries-card">
          <div className="p-6 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Warning size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-700 font-semibold">DOUBLE-COUNT WARNING</div>
              <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
                Historical revenue summaries detected
              </div>
              <div className="text-[12px] text-slate-600 mt-0.5 mb-3">
                You have <span className="font-mono font-bold text-amber-800">{histStats.count}</span> aggregated summary records totalling <span className="font-mono font-bold text-amber-800">${histStats.revenue_sum.toLocaleString()}</span>. If you've also imported per-invoice detail for the same period, Reports will ADD both → revenue is inflated. Clear these summaries to let the individual invoices be the single source of truth.
              </div>
              <button
                onClick={clearHistSummaries}
                disabled={loadingHist || histStats.count === 0}
                data-testid="clear-hist-summaries-btn"
                className="h-10 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash size={13} weight="bold" />
                Clear all {histStats.count} historical summary records
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Recent imports */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="imports-card">
        <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#1E5FEB] flex items-center justify-center">
            <UsersIcon size={18} weight="duotone" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">CUSTOMER DATA</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Recent imports — Undo if needed
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Customers are safe to undo as long as they haven't been used on a ticket, sale, or booking yet. Customers with transactions are always preserved.
            </div>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No imports recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="p-3">When</th>
                <th className="p-3">By</th>
                <th className="p-3 text-right">Imported</th>
                <th className="p-3 text-right">Still tagged</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-32 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-b border-[#E5E7EB] last:border-b-0" data-testid={`batch-row-${b.id}`}>
                  <td className="p-3 font-mono text-[12px]">{fmtDate(b.imported_at)}</td>
                  <td className="p-3 text-slate-600">{b.imported_by || "—"}</td>
                  <td className="p-3 text-right font-mono tabular-nums">{b.count}</td>
                  <td className="p-3 text-right font-mono tabular-nums">{b.remaining}</td>
                  <td className="p-3">
                    {b.undone_at
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full"><CheckCircle size={11} weight="bold" /> UNDONE</span>
                      : b.remaining === 0
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full">ALL TRANSACTED</span>
                        : <span className="text-[11px] text-slate-500">Active</span>}
                  </td>
                  <td className="p-3 text-right">
                    {!b.undone_at && b.remaining > 0 && (
                      <button
                        onClick={() => undoBatch(b)}
                        data-testid={`undo-batch-${b.id}`}
                        className="h-8 px-3 rounded-lg border border-slate-200 hover:border-[#DC2626] hover:text-[#DC2626] text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1.5"
                      >
                        <ArrowCounterClockwise size={12} weight="bold" /> Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Recent invoice imports */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="sale-imports-card">
        <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Database size={18} weight="duotone" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">INVOICE DATA</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Recent invoice imports — Undo if needed
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Deletes only the invoices that came in through this batch. POS-entered sales from the same day stay untouched. Corresponding customers aren't removed (use the card above for that).
            </div>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : saleBatches.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No invoice imports recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="p-3">When</th>
                <th className="p-3">By</th>
                <th className="p-3 text-right">Imported</th>
                <th className="p-3 text-right">Still present</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-32 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {saleBatches.map((b) => (
                <tr key={b.id} className="border-b border-[#E5E7EB] last:border-b-0" data-testid={`sale-batch-row-${b.id}`}>
                  <td className="p-3 font-mono text-[12px]">{fmtDate(b.imported_at)}</td>
                  <td className="p-3 text-slate-600">{b.imported_by || "—"}</td>
                  <td className="p-3 text-right font-mono tabular-nums">{b.count}</td>
                  <td className="p-3 text-right font-mono tabular-nums">{b.remaining}</td>
                  <td className="p-3">
                    {b.undone_at
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full"><CheckCircle size={11} weight="bold" /> UNDONE</span>
                      : b.remaining === 0
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-full">EMPTY</span>
                        : <span className="text-[11px] text-slate-500">Active</span>}
                  </td>
                  <td className="p-3 text-right">
                    {!b.undone_at && b.remaining > 0 && (
                      <button
                        onClick={() => undoSaleBatch(b)}
                        data-testid={`undo-sale-batch-${b.id}`}
                        className="h-8 px-3 rounded-lg border border-slate-200 hover:border-[#DC2626] hover:text-[#DC2626] text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1.5"
                      >
                        <ArrowCounterClockwise size={12} weight="bold" /> Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Unify legacy invoice numbers */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="unify-invoices-card">
        <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
            <Database size={18} weight="duotone" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">ONE INVOICE SYSTEM</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Renumber legacy INV- invoices to S-
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Old Stripe/online invoices created before invoices were unified may still carry a legacy INV-XXXXX number. This renumbers them to the same S-XXXXXX series as every other invoice — one system, no duplicates. Safe to run anytime; does nothing if there's nothing left to fix.
            </div>
          </div>
        </div>
        <div className="p-6">
          <button
            onClick={() => setPinPrompt({ kind: "unify-invoices", payload: null, label: "Renumber all legacy INV- invoices to the S- series?", confirmLabel: "Renumber invoices", destructive: false })}
            disabled={unifying}
            data-testid="unify-invoices-btn"
            className="h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <ArrowCounterClockwise size={13} weight="bold" />
            {unifying ? "Renumbering…" : "Unify invoice numbers now"}
          </button>
        </div>
      </section>
      {/* Fix duplicate invoices */}
      {isAdmin && <OverpaidPaymentsCard />}

      <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="dup-invoices-card">
        <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <Warning size={18} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">DOUBLE-CHARGE CLEANUP</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Fix tickets with duplicate invoices
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              Finds repair tickets that ended up with more than one active invoice (e.g. two paid $379 invoices for one $379 repair). Void the extra one to fix the record — we keep the earliest and flag any amount that may need refunding.
            </div>
          </div>
          <button
            onClick={scanDuplicates}
            disabled={dupLoading}
            data-testid="scan-duplicates-btn"
            className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 shrink-0"
          >
            <Eye size={13} weight="bold" />
            {dupLoading ? "Scanning…" : "Scan for duplicates"}
          </button>
        </div>
        {dupScan && (
          <div className="p-6 space-y-4">
            {dupScan.count === 0 ? (
              <div className="flex items-center gap-2 text-[13px] text-emerald-700 font-semibold">
                <CheckCircle size={16} weight="fill" /> No duplicate invoices — every ticket is clean.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-[12px] font-semibold text-slate-600">
                    {dupScan.count} ticket{dupScan.count === 1 ? "" : "s"} with duplicate invoices · {dupSelected.size} selected
                  </div>
                  <button
                    onClick={() => {
                      if (dupSelected.size === 0) { toast.info("Select at least one invoice to void"); return; }
                      setPinPrompt({ kind: "void-duplicate-bulk", payload: Array.from(dupSelected), label: `Void ${dupSelected.size} selected duplicate invoice${dupSelected.size === 1 ? "" : "s"}? The kept invoice on each ticket stays.`, confirmLabel: `Void ${dupSelected.size} selected`, destructive: true });
                    }}
                    disabled={dupSelected.size === 0}
                    data-testid="void-selected-dups-btn"
                    className="h-9 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40 shrink-0"
                  >
                    <Trash size={13} weight="bold" /> Void selected ({dupSelected.size})
                  </button>
                </div>
                {dupScan.tickets.map((t) => (
                  <div key={t.ticket_id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`dup-group-${t.ticket_number}`}>
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <span className="font-mono font-bold text-[13px] text-slate-900">{t.ticket_number || "—"}</span>
                        <span className="text-[12px] text-slate-500 ml-2">{t.customer_name} · {t.device}</span>
                      </div>
                      {t.overpaid > 0.005 && (
                        <span className="text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
                          Overpaid ${t.overpaid.toFixed(2)} (paid ${t.total_paid.toFixed(2)} / cost ${t.repair_cost.toFixed(2)})
                        </span>
                      )}
                    </div>
                    <div className="divide-y divide-slate-100">
                      {t.invoices.map((inv) => {
                        const isKeep = inv.id === t.keep_id;
                        const checked = dupSelected.has(inv.id);
                        return (
                          <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`dup-inv-${inv.invoice_number}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleDupSelect(inv.id)}
                                data-testid={`dup-check-${inv.invoice_number}`}
                                className="w-4 h-4 rounded border-slate-300 text-[#DC2626] focus:ring-[#DC2626] cursor-pointer shrink-0"
                              />
                              <span className="font-mono text-[13px] font-semibold text-slate-800">{inv.invoice_number}</span>
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{inv.status}</span>
                              <span className="text-[12px] text-slate-500">${inv.total.toFixed(2)}{inv.paid > 0.005 ? ` · paid $${inv.paid.toFixed(2)}` : ""}</span>
                            </div>
                            {isKeep ? (
                              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 shrink-0">KEEP (earliest)</span>
                            ) : (
                              <button
                                onClick={() => setPinPrompt({ kind: "void-duplicate", payload: inv, label: `Void duplicate invoice ${inv.invoice_number}? The earliest invoice is kept.`, confirmLabel: "Void duplicate", destructive: true })}
                                data-testid={`void-dup-${inv.invoice_number}`}
                                className="h-8 px-3 rounded-lg bg-white border border-[#DC2626] text-[#DC2626] hover:bg-rose-50 text-[11px] font-bold inline-flex items-center gap-1.5 shrink-0"
                              >
                                <Trash size={12} weight="bold" /> Void this
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </section>
      {/* Duplicate POS invoices (sales ledger) — a ticket rung up twice */}
      {saleDupScan && (
        <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="dup-sales-card">
          <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Warning size={18} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">DOUBLE-CHARGE CLEANUP</div>
              <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
                Tickets rung up twice at POS
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Repairs that ended up with two POS invoices (e.g. S-000234 paid + S-000235 unpaid for one $399 repair). Void the extra one — the invoice holding the money is kept.
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {saleDupScan.count === 0 ? (
              <div className="flex items-center gap-2 text-[13px] text-emerald-700 font-semibold">
                <CheckCircle size={16} weight="fill" /> No ticket is billed twice at POS.
              </div>
            ) : (
              saleDupScan.tickets.map((t) => (
                <div key={t.ticket_id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`dup-sale-group-${t.ticket_number}`}>
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="font-mono font-bold text-[13px] text-slate-900">{t.ticket_number || "—"}</span>
                      <span className="text-[12px] text-slate-500 ml-2">{t.customer_name} · {t.device}</span>
                    </div>
                    {t.overpaid > 0.005 && (
                      <span className="text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
                        Overpaid ${t.overpaid.toFixed(2)} (paid ${t.total_paid.toFixed(2)} / cost ${t.repair_cost.toFixed(2)})
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {t.sales.map((sale) => (
                      <div key={sale.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`dup-sale-${sale.sale_number}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-[13px] font-semibold text-slate-800">{sale.sale_number}</span>
                          <span className="text-[12px] text-slate-500">
                            ${sale.total.toFixed(2)}{sale.paid > 0.005 ? ` · paid $${sale.paid.toFixed(2)}` : " · unpaid"}
                            {sale.balance > 0.005 ? ` · $${sale.balance.toFixed(2)} owing` : ""}
                          </span>
                        </div>
                        {sale.id === t.keep_id ? (
                          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 shrink-0">KEEP (has the payment)</span>
                        ) : (
                          <button
                            onClick={() => setPinPrompt({ kind: "void-duplicate-sale", payload: sale, label: `Void duplicate invoice ${sale.sale_number}? The invoice holding the payment is kept.`, confirmLabel: "Void duplicate", destructive: true })}
                            data-testid={`void-dup-sale-${sale.sale_number}`}
                            className="h-8 px-3 rounded-lg bg-white border border-[#DC2626] text-[#DC2626] hover:bg-rose-50 text-[11px] font-bold inline-flex items-center gap-1.5 shrink-0"
                          >
                            <Trash size={12} weight="bold" /> Void this
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="date-cleanup-card">
        <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <CalendarBlank size={18} weight="duotone" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">ONE-SHOT CLEANUP</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              Delete customers created in a date range
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              For bad historical imports that happened before batch tagging. Preview first — customers with any ticket, sale, or booking are automatically preserved.
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">From (inclusive)</div>
              <input
                type="date"
                value={since}
                onChange={(e) => { setSince(e.target.value); setPreview(null); }}
                data-testid="cleanup-since"
                className="h-10 px-3 border border-[#E5E7EB] rounded-lg font-mono text-sm focus:border-[#1E5FEB] focus:outline-none"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">To (inclusive)</div>
              <input
                type="date"
                value={until}
                onChange={(e) => { setUntil(e.target.value); setPreview(null); }}
                data-testid="cleanup-until"
                className="h-10 px-3 border border-[#E5E7EB] rounded-lg font-mono text-sm focus:border-[#1E5FEB] focus:outline-none"
              />
            </label>
            <button
              onClick={runPreview}
              disabled={previewing}
              data-testid="cleanup-preview-btn"
              className="h-10 px-4 rounded-lg bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Eye size={13} weight="bold" /> {previewing ? "Checking…" : "Preview"}
            </button>
          </div>

          {/* Safety filters */}
          <div className="flex flex-wrap gap-4 rounded-xl bg-amber-50/60 border border-amber-200 p-3">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={importedOnly}
                onChange={(e) => { setImportedOnly(e.target.checked); setPreview(null); }}
                data-testid="cleanup-imported-only"
                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-[#1E5FEB] focus:ring-[#1E5FEB]"
              />
              <div>
                <div className="text-[12px] font-semibold text-slate-800">Only customers from imports</div>
                <div className="text-[11px] text-slate-600">
                  Recommended. Ignores POS-entered customers in the window — only customers created by a CSV / XLSX import are considered.
                </div>
              </div>
            </label>
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteImportedSales}
                onChange={(e) => { setDeleteImportedSales(e.target.checked); setPreview(null); }}
                data-testid="cleanup-delete-imported-sales"
                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-[#DC2626] focus:ring-[#DC2626]"
              />
              <div>
                <div className="text-[12px] font-semibold text-slate-800">Also delete imported invoices in this window</div>
                <div className="text-[11px] text-slate-600">
                  Removes RepairDesk-imported sales in the same window. POS-entered sales are never touched.
                </div>
              </div>
            </label>
          </div>

          {preview && (
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4">
              {preview.total_in_window === 0 && (!deleteImportedSales || !preview.imported_sales_count) ? (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <CheckCircle size={16} weight="bold" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">Nothing to delete in this window</div>
                    <div className="text-[12px] text-slate-500">
                      No {importedOnly ? "imported " : ""}customers{deleteImportedSales ? " or imported invoices" : ""} were created between {since} and {until}. Try expanding the range or unticking "Only customers from imports".
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                        {importedOnly ? "Imported customers in window" : "Customers in window"}
                      </div>
                      <div className="font-mono font-bold text-2xl text-slate-900 tabular-nums">{preview.total_in_window}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Deletable</div>
                      <div className="font-mono font-bold text-2xl text-emerald-600 tabular-nums">{preview.deletable}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Preserved (transacted)</div>
                      <div className="font-mono font-bold text-2xl text-slate-500 tabular-nums">{preview.locked}</div>
                    </div>
                    {deleteImportedSales && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-rose-600 font-semibold">Imported invoices to delete</div>
                        <div className="font-mono font-bold text-2xl text-rose-600 tabular-nums">{preview.imported_sales_count || 0}</div>
                      </div>
                    )}
                    <div className="ml-auto">
                      {(preview.deletable > 0 || (deleteImportedSales && preview.imported_sales_count > 0)) ? (
                        <button
                          onClick={deleteByDate}
                          data-testid="cleanup-delete-btn"
                          className="h-10 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-bold inline-flex items-center gap-1.5"
                        >
                          <Trash size={13} weight="bold" /> Delete {preview.deletable} customers{deleteImportedSales && preview.imported_sales_count > 0 ? ` + ${preview.imported_sales_count} invoices` : ""}
                        </button>
                      ) : (
                        <div className="text-[12px] text-zinc-500 italic">Nothing to delete</div>
                      )}
                    </div>
                  </div>
                  {preview.sample?.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-[11px] text-[#1E5FEB] font-semibold hover:underline">
                        Show {Math.min(preview.sample.length, 20)} example rows
                      </summary>
                      <div className="mt-2 text-[11px] font-mono text-slate-600 space-y-1">
                        {preview.sample.map((c) => (
                          <div key={c.id} className="flex items-center gap-3">
                            <span className="text-slate-400">{fmtDate(c.created_at)}</span>
                            <span className="font-medium">{c.name}</span>
                            {c.phone && <span className="text-slate-500">· {c.phone}</span>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {pinPrompt && (
        <PinDialog
          label={pinPrompt.label}
          confirmLabel={pinPrompt.confirmLabel}
          destructive={pinPrompt.destructive !== false}
          onConfirm={submitPin}
          onCancel={() => setPinPrompt(null)}
        />
      )}
    </div>
  );
}

function PinDialog({ label, confirmLabel, destructive = true, onConfirm, onCancel }) {
  const [pin, setPin] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${destructive ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-[#1E5FEB]"}`}>
            <Warning size={18} weight="duotone" />
          </div>
          <div>
            <div className="font-bold text-slate-900">Confirm with admin PIN</div>
            <div className="text-[12px] text-slate-500 mt-0.5">{label}</div>
          </div>
        </div>
        <input
          autoFocus
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && onConfirm(pin)}
          placeholder="••••"
          data-testid="pin-input"
          className="w-full h-12 px-4 rounded-lg border-2 border-[#1E5FEB] text-center font-mono font-bold text-2xl tracking-[0.4em] focus:outline-none focus:ring-4 focus:ring-blue-100"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-slate-200 text-slate-600 text-[12px] font-semibold hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(pin)}
            disabled={pin.length < 4}
            data-testid="pin-confirm"
            className={`h-10 px-4 rounded-lg text-white text-[12px] font-bold disabled:opacity-40 ${destructive ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#1E5FEB] hover:bg-[#1849C8]"}`}
          >
            {confirmLabel || "Confirm delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
